import { Link, useNavigate } from "react-router";
import styled from "styled-components";
import logo from "../../assets/logo.svg";
import { getShopCartQuantity, useShopCartStore } from "../../stores/shopCart.store";
import { useProfileOwner, useUserStore } from "../../stores/user.store";
import { useShopAccess } from "../../pages/shop/useShopAccess";
import { ShopBagIcon } from "./ShopBagIcon";
import { UserButton } from "./UserButton";

export type HeaderProps = {
  onMenuClick: () => void;
  isOpen: boolean;
};

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  z-index: 100;
  background-color: black;
`;

const MenuButton = styled.button<{ $isOpen: boolean }>`
  background: none;
  border: none;
  cursor: pointer;
  width: 32px;
  height: 32px;
  position: relative;
  margin-right: 10px;

  span {
    display: block;
    position: absolute;
    width: 24px;
    height: 2px;
    background-color: white;
    transition: all 0.3s ease;

    &:first-child {
      top: ${(props) => (props.$isOpen ? "15px" : "8px")};
      transform: ${(props) => (props.$isOpen ? "rotate(45deg)" : "rotate(0)")};
    }

    &:nth-child(2) {
      top: 15px;
      opacity: ${(props) => (props.$isOpen ? "0" : "1")};
    }

    &:last-child {
      top: ${(props) => (props.$isOpen ? "15px" : "22px")};
      transform: ${(props) => (props.$isOpen ? "rotate(-45deg)" : "rotate(0)")};
    }
  }
`;

const Logo = styled.img`
  height: 40px;
  margin-left: 10px;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;

  @media (max-width: 767px) {
    gap: 10px;
  }
`;

const ShopNav = styled.div`
  align-items: center;
  display: inline-flex;
  gap: 12px;
`;

const ShopLink = styled(Link)`
  align-items: center;
  color: white;
  cursor: pointer;
  display: inline-flex;
  text-decoration: none;
  text-transform: uppercase;
`;

const ShopIcon = styled.span`
  display: none;

  @media (max-width: 767px) {
    display: inline-flex;
  }
`;

// The bag is its own link so the label still opens the catalogue while the
// bag jumps straight to the cart.
const CartLink = styled(Link)`
  align-items: center;
  color: white;
  cursor: pointer;
  display: inline-flex;
  text-decoration: none;
`;

const ShopLabel = styled.span`
  font-size: 16px;
  font-weight: 700;
  line-height: 1;

  @media (max-width: 767px) {
    display: none;
  }
`;

const StorefrontIcon = () => {
  return (
    <svg
      aria-hidden="true"
      fill="none"
      height="31"
      stroke="currentColor"
      strokeWidth="1.9"
      viewBox="0 0 24 24"
      width="31"
    >
      <path d="M4 10h16l-1 10H5L4 10Z" />
      <path d="M7 10V8a5 5 0 0 1 10 0v2" />
      <path d="M3 10l2-5h14l2 5" />
    </svg>
  );
};

const formatShopCartBadgeQuantity = (quantity: number) => {
  if (quantity <= 0) {
    return null;
  }

  if (quantity > 9) {
    return "9+";
  }

  return String(quantity);
};

export const Header = ({ onMenuClick, isOpen }: HeaderProps) => {
  const { isLoading } = useUserStore();
  const navigate = useNavigate();
  const profileOwner = useProfileOwner();
  const cartQuantity = useShopCartStore((state) => {
    return getShopCartQuantity(state.items);
  });
  // The cart belongs to tesserati only, so nobody else sees the bag.
  const canUseCart = useShopAccess() === "member";
  const cartBadgeLabel = canUseCart
    ? formatShopCartBadgeQuantity(cartQuantity)
    : null;
  const cartLinkTarget = cartQuantity > 0 ? "/shop/cart" : "/shop";

  return (
    <HeaderContainer>
      <Logo src={logo} alt="CO2 Snowboarding" onClick={() => navigate("/")} />
      <RightSection>
        <ShopNav>
          <ShopLink to="/shop" aria-label="Shop">
            <ShopLabel>SHOP</ShopLabel>
            <ShopIcon>
              <StorefrontIcon />
            </ShopIcon>
          </ShopLink>
          {canUseCart && (
            <CartLink
              to={cartLinkTarget}
              aria-label={`Carrello, ${cartQuantity} articoli`}
            >
              <ShopBagIcon badgeLabel={cartBadgeLabel} />
            </CartLink>
          )}
        </ShopNav>
        {!isLoading && (
          <UserButton
            isLoggedIn={!!profileOwner}
            username={profileOwner?.first_name}
          />
        )}
        <MenuButton onClick={onMenuClick} $isOpen={isOpen}>
          <span></span>
          <span></span>
          <span></span>
        </MenuButton>
      </RightSection>
    </HeaderContainer>
  );
};
